<template>
  <div>
    <v-row
      align="center"
      class="temp-code-history__toolbar pa-4"
      :class="hasClientContext ? 'pb-3' : 'pb-2'"
      dense
    >
      <v-card-title class="temp-code-history__title py-0">
        {{ title }}
      </v-card-title>
      <v-spacer />
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            color="#4C6C93"
            class="border card-footer mt-1 mb-2"
            v-bind="attrs"
            v-on="on"
            @click="loadHistory"
          >
            <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>Обновить</span>
      </v-tooltip>
    </v-row>
    <v-row v-if="!hasClientContext" class="temp-code-history__filter-row px-4 pb-3" dense>
      <v-col cols="6" sm="5" md="4" lg="3" xl="3" class="py-0">
        <v-select
          v-model="channelFilter"
          class="temp-code-history__source-select"
          :items="channelFilterItems"
          placeholder="Источник"
          clearable
          dense
          outlined
          rounded
          color="primary"
          hide-details
          item-text="text"
          item-value="value"
          prepend-inner-icon="mdi-filter-variant"
          :menu-props="{ offsetY: true, maxHeight: 280, contentClass: 'temp-code-history__source-menu' }"
          @input="onChannelFilterChange"
        />
      </v-col>
      <v-col cols="6" sm="5" md="4" lg="3" xl="3" class="py-0">
        <v-text-field
          v-model="addressFilter"
          class="temp-code-history__source-select"
          placeholder="Адрес домофона"
          clearable
          dense
          outlined
          rounded
          color="primary"
          hide-details
          prepend-inner-icon="mdi-map-marker-outline"
          @keyup.enter="onAddressFilterChange"
          @click:clear="onAddressFilterChange"
        />
      </v-col>
    </v-row>
    <v-data-table
      class="table"
      :headers="headers"
      :items="rows"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, 100],
        showFirstLastPage: true,
      }"
    >
      <template #item.event_at="{ item }">
        {{ ruDateFormat(item.event_at) }}
      </template>
      <template #item.channel_label="{ item }">
        {{ item.channel_label }}
      </template>
      <template #item.doorphone_address="{ item }">
        <template v-if="includeDoorphoneAddress && item.doorphone_summary_id && $canRoute('DetailDoorphone')">
          <router-link
            class="routerLink"
            :to="{ name: 'DetailDoorphone', params: { id: String(item.doorphone_summary_id) } }"
          >
            {{ item.doorphone_address }}
          </router-link>
        </template>
        <template v-else>
          {{ item.doorphone_address }}
        </template>
      </template>
      <template #item.user_name="{ item }">
        <template v-if="item.callCenterLike">
          {{ item.issuerOperatorLine }}
        </template>
        <template v-else-if="item.mobileChannel">
          <span class="issuer-client">
            Клиент
            <template v-if="item.clientTelephone">
              <span class="issuer-client__sep"> · </span>
              <template v-if="item.client_id && $canRoute('DetailClient')">
                <router-link
                  v-if="!isSameClientRow(item)"
                  class="routerLink"
                  :to="{ name: 'DetailClient', params: { id: String(item.client_id) } }"
                >
                  {{ formatPhoneDisplay(item.clientTelephone) }}
                </router-link>
                <span v-else>{{ formatPhoneDisplay(item.clientTelephone) }}</span>
              </template>
              <template v-else>
                {{ formatPhoneDisplay(item.clientTelephone) }}
              </template>
            </template>
          </span>
        </template>
        <template v-else>
          {{ item.user_name }}
        </template>
      </template>
      <template #item.detail_info="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                icon
                small
                color="primary"
                class="ma-0"
                :disabled="item.mobileChannel"
                aria-label="Подробная информация о выдаче кода"
                @click="openProvisionDetail(item)"
              >
                <v-icon dense>mdi-information-outline</v-icon>
              </v-btn>
            </span>
          </template>
          <span>{{ item.mobileChannel ? 'Недоступно для мобильного приложения' : 'Подробная информация' }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="provisionDetailDialog"
      max-width="520"
      content-class="dialog-border-radius"
      @click:outside="closeProvisionDetail"
    >
      <v-card v-if="provisionDetailItem">
        <v-card-title class="text-h6">
          Подробная информация
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="detail-row">
            <span class="detail-row__label">Телефон</span>
            <span class="detail-row__value">
              <template v-if="provisionDetailItem.client_id && $canRoute('DetailClient')">
                <router-link
                  v-if="!isSameClientRow(provisionDetailItem)"
                  class="routerLink"
                  :to="{ name: 'DetailClient', params: { id: String(provisionDetailItem.client_id) } }"
                >
                  {{ formatPhoneDisplay(provisionDetailItem.telephone) }}
                </router-link>
                <span v-else>{{ formatPhoneDisplay(provisionDetailItem.telephone) }}</span>
              </template>
              <template v-else>
                {{ formatPhoneDisplay(provisionDetailItem.telephone) }}
              </template>
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-row__label">ФИО</span>
            <span class="detail-row__value">{{ provisionDetailItem.fio }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-row__label">Причина</span>
            <span class="detail-row__value">{{ provisionDetailItem.reason }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-row__label">Часы</span>
            <span class="detail-row__value">{{ provisionDetailItem.hours }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-row__label">Тип открытия</span>
            <span class="detail-row__value">{{ formatOpeningType(provisionDetailItem.code_type) }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text class="text-none" @click="closeProvisionDetail">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import watchTableOptions from '@/mixins/watchTableOptions';
import { DEFAULT_DATA_TABLE_OPTIONS } from '@/constants/data-table';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import formatPhoneNumber from '@/mixins/formatPhoneNumber';
import {
  tempCodeProvisionHistoryHeaders,
  tempCodeProvisionHistoryHeadersWithAddress,
} from '../consts';

const MOBILE_CHANNELS = new Set(['mobile_app', 'mobile', 'app']);

const CHANNEL_LABELS = {
  call_center: 'Кол-центр',
  mobile_app: 'Мобильное приложение',
  mobile: 'Мобильное приложение',
  app: 'Мобильное приложение',
};

const ROLE_ID_LABEL = {
  1: 'Администратор',
  5: 'Админ-инженер',
  6: 'Менеджер',
  7: 'Аналитик',
  8: 'Кол-центр',
  10: 'Мастер',
  11: 'Администратор КПП',
};

function isChannelEmpty (ch) {
  return ch === null || ch === undefined || String(ch).trim() === '';
}

function resolveActorUser (action) {
  const wrap = action.user;
  if (!wrap) {
    return null;
  }
  if (wrap.user && typeof wrap.user === 'object') {
    return wrap.user;
  }
  if (typeof wrap === 'object' && (wrap.name !== undefined || wrap.id !== undefined)) {
    return wrap;
  }
  return null;
}

function roleLabel (roleId) {
  if (roleId === null || roleId === undefined || roleId === '') {
    return '';
  }
  const n = Number(roleId);
  if (Number.isNaN(n)) {
    return String(roleId);
  }
  return ROLE_ID_LABEL[n] || `роль ${n}`;
}

function formatOperatorLine (u) {
  if (!u) {
    return '—';
  }
  const name = u.name || '—';
  const role = roleLabel(u.role_id);
  return role ? `${name} (${role})` : name;
}

export default {
  name: 'TempCodeProvisionHistoryJournal',
  mixins: [watchTableOptions, ruDateFormatMixin, formatPhoneNumber],
  props: {
    doorphoneId: {
      type: [Number, String],
      default: null,
    },
    includeDoorphoneAddress: {
      type: Boolean,
      default: false,
    },
    /** Фильтр по клиенту (карточка клиента); запрос без doorphoneId */
    clientId: {
      type: [Number, String],
      default: null,
    },
  },
  data () {
    return {
      options: { ...DEFAULT_DATA_TABLE_OPTIONS },
      total: 0,
      loading: false,
      provisionDetailDialog: false,
      provisionDetailItem: null,
      channelFilter: '',
      addressFilter: '',
      channelFilterItems: [
        { text: 'Все', value: '' },
        { text: 'Кол-центр', value: 'call_center' },
        { text: 'Мобильное приложение', value: 'mobile_app' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      rawItems: 'Doorphone/tempCodeProvisionHistory',
    }),
    hasClientContext () {
      return this.clientId != null && this.clientId !== '';
    },
    showAddressColumn () {
      return this.includeDoorphoneAddress || this.hasClientContext;
    },
    title () {
      if (this.hasClientContext || this.doorphoneId) {
        return 'Журнал кодов';
      }
      return 'Журнал предоставления кода открытия (все домофоны)';
    },
    headers () {
      return this.showAddressColumn
        ? tempCodeProvisionHistoryHeadersWithAddress
        : tempCodeProvisionHistoryHeaders;
    },
    rows () {
      return (Array.isArray(this.rawItems) ? this.rawItems : []).map((entry) => {
        const action = entry && entry.action !== undefined ? entry.action : entry;
        const data = action.data || {};
        const chRaw = data.channel;
        const callCenterLike = chRaw === 'call_center' || isChannelEmpty(chRaw);
        const mobileChannel = !callCenterLike && MOBILE_CHANNELS.has(chRaw);
        const actor = resolveActorUser(action);

        let channelLabel;
        if (callCenterLike) {
          channelLabel = 'Кол-центр';
        } else if (CHANNEL_LABELS[chRaw]) {
          channelLabel = CHANNEL_LABELS[chRaw];
        } else if (chRaw) {
          channelLabel = String(chRaw);
        } else {
          channelLabel = '—';
        }

        const clientTelephone = actor && (actor.telephone != null && actor.telephone !== '')
          ? String(actor.telephone)
          : '';

        const clientId =
          action.client_id != null
            ? action.client_id
            : (data.client_id != null ? data.client_id : null);
        const summary = action.doorphone_summary;
        const doorphoneSummaryId = summary && summary.id != null ? summary.id : null;
        const doorphoneAddress = summary && summary.address != null && summary.address !== ''
          ? String(summary.address)
          : '—';

        return {
          id: action.id,
          event_at: action.code_received_at || action.created_at,
          channel_label: channelLabel,
          callCenterLike,
          mobileChannel,
          apartment: data.apartment != null ? data.apartment : '—',
          telephone: data.telephone != null ? String(data.telephone) : '—',
          fio: data.fio || '—',
          reason: data.reason || '—',
          hours: data.hours != null ? data.hours : '—',
          code_type: data.type != null ? data.type : '—',
          user_name: actor?.name || '—',
          clientTelephone,
          client_id: clientId,
          issuerOperatorLine: formatOperatorLine(actor),
          detail_info: null,
          doorphone_summary_id: doorphoneSummaryId,
          doorphone_address: doorphoneAddress,
        };
      });
    },
  },
  created () {
    this.loadHistory();
  },
  methods: {
    ...mapActions({
      fetchTempCodeProvisionHistory: 'Doorphone/fetchTempCodeProvisionHistory',
    }),
    isSameClientRow (item) {
      if (!item || !this.hasClientContext) {
        return false;
      }
      return String(item.client_id) === String(this.clientId);
    },
    buildHistoryFilters () {
      const filters = {};
      if (this.hasClientContext) {
        filters.client_id = this.clientId;
        filters.channel = 'mobile_app';
        return filters;
      }
      if (this.channelFilter) {
        filters.channel = this.channelFilter;
      }
      if (this.addressFilter && this.addressFilter.trim()) {
        filters.address = this.addressFilter.trim();
      }
      return filters;
    },
    onChannelFilterChange () {
      this.$set(this.options, 'page', 1);
      this.loadHistory();
    },
    onAddressFilterChange () {
      this.$set(this.options, 'page', 1);
      this.loadHistory();
    },
    formatOpeningType (type) {
      const t = type != null ? String(type).trim().toUpperCase() : '';
      if (t === 'C') {
        return 'Общий код';
      }
      if (t === 'I') {
        return 'Код универсальный';
      }
      return type != null && type !== '' ? String(type) : '—';
    },
    formatPhoneDisplay (phone) {
      if (phone == null || phone === '' || phone === '—') {
        return '—';
      }
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length < 10) {
        return String(phone);
      }
      try {
        return this.formatPhoneNumber(phone);
      } catch (e) {
        return String(phone);
      }
    },
    openProvisionDetail (item) {
      if (item.mobileChannel) {
        return;
      }
      this.provisionDetailItem = { ...item };
      this.provisionDetailDialog = true;
    },
    closeProvisionDetail () {
      this.provisionDetailDialog = false;
      this.$nextTick(() => {
        this.provisionDetailItem = null;
      });
    },
    loadHistory () {
      this.loading = true;
      this.fetchTempCodeProvisionHistory({
        doorphoneId: this.doorphoneId,
        page: this.options.page,
        perPage: this.options.itemsPerPage,
        filters: this.buildHistoryFilters(),
      })
        .then((payload) => {
          const meta = payload && payload.meta ? payload.meta : {};
          this.total = meta.total != null ? meta.total : 0;
        })
        .catch((err) => {
          console.error(err);
          this.$flash_warning('Не удалось загрузить журнал предоставления кодов');
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    clientId () {
      this.$set(this.options, 'page', 1);
      this.loadHistory();
    },
    doorphoneId () {
      this.loadHistory();
    },
    options (curr, prev) {
      this.handleOptionsChange(curr, prev, () => {
        this.loadHistory();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.temp-code-history__toolbar {
  flex-wrap: nowrap;
}
.temp-code-history__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.125rem;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.35;
}
.temp-code-history__filter-row {
  align-items: flex-start;
}
.temp-code-history__source-select {
  margin: 0;
  padding-top: 0;
  font-size: 0.8125rem;
}
.temp-code-history__source-select ::v-deep .v-input__control {
  min-height: 36px;
}
.temp-code-history__source-select ::v-deep .v-input__slot {
  min-height: 36px !important;
  padding: 0 8px 0 4px !important;
  border-color: rgba(0, 0, 0, 0.14) !important;
  background: #fafafa;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.temp-code-history__source-select.v-input--is-focused ::v-deep .v-input__slot {
  background: #fff;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.25);
}
.temp-code-history__source-select ::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center;
  padding-right: 4px;
}
.temp-code-history__source-select ::v-deep .v-input__prepend-inner .v-icon {
  font-size: 18px !important;
  color: #78909c !important;
}
.temp-code-history__source-select ::v-deep .v-select__selection,
.temp-code-history__source-select ::v-deep .v-select__selection--comma {
  font-size: 0.8125rem;
  line-height: 1.25;
  color: #37474f;
}
.temp-code-history__source-select ::v-deep .v-select__slot {
  min-height: 34px;
}
.temp-code-history__source-select ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center;
  padding-top: 0 !important;
}
.temp-code-history__source-select ::v-deep .v-label {
  font-size: 0.75rem;
}
.border {
  border-radius: 16px;
}
.card-footer {
  background-color: #4C6C93;
  color: white;
  width: 65px !important;
  margin-right: 15px;
  height: 65px !important;
  padding: 5px 16px;
  min-width: 65px !important;
}

.detail-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.9375rem;
  &:last-child {
    border-bottom: none;
  }
}
.detail-row__label {
  flex: 0 0 120px;
  color: #546e7a;
  font-weight: 500;
}
.detail-row__value {
  flex: 1 1 200px;
  min-width: 0;
  word-break: break-word;
}
.issuer-client {
  font-size: 0.875rem;
}
.issuer-client__sep {
  color: #90a4ae;
}

.routerLink {
  text-decoration: none;
}

.v-application a {
  color: #434850;
}

.row-click {
  cursor: pointer;
}

a:hover.routerLink {
  text-decoration: underline;
}
</style>

<style lang="scss">
/* меню v-select монтируется вне scoped-обёртки */
.temp-code-history__source-menu .v-list-item {
  min-height: 36px;
  font-size: 0.8125rem;
}
.temp-code-history__source-menu .v-list-item__title {
  font-size: 0.8125rem;
  line-height: 1.3;
}
</style>
